// #845 — Admin Persona sub-page (read-only).
//
// QA-observability surface that closes the loop on #362's companion persona:
// admin can now open `/admin/users/<id>/persona` and see exactly which likes /
// dislikes / quirks / boundaries Berta carries into THIS user's calls, then
// compare against what she actually said in the live conversation.
//
// Read-only by design. No edit / delete / regenerate affordance:
//   - Editing the persona is Phase B of #362.
//   - Per-user persona storage doesn't exist yet — every user sees the same
//     DEFAULT_PERSONA. The "shared across all users" disclaimer makes that
//     reality explicit so admin doesn't mistake the surface for per-user
//     config.
//   - The endpoint shape (`/users/<id>/persona`) is per-user from day one
//     so a future Phase-B story can swap the source without touching the
//     frontend or the API contract.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { adminApi, adminGet } from './adminApi';
import { userSubPageStyles } from './userSubPageStyles';

const VERSION = 'v1.0.0';

// Page-local additions on top of the shared sub-page styles.
// The shared styles already give us .page-header / .section-title /
// .back-link / .empty-state / .loading-state / .error-state / .ver.
// Here we add the persona-list look: a card-shaped section body that
// renders likes/dislikes/quirks as a list with a "why" annotation.
const styles = `
  ${userSubPageStyles}
  .persona-section {
    background: #fff; border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 16px 20px; margin-bottom: 16px;
  }
  .persona-list { list-style: none; padding: 0; margin: 0; }
  .persona-list li {
    padding: 10px 0; border-bottom: 1px solid #F0EDE8;
    font-size: 0.9rem; color: #2C2C2C; line-height: 1.45;
  }
  .persona-list li:last-child { border-bottom: none; }
  .persona-topic { font-weight: 600; }
  .persona-why { color: #888; font-weight: 400; }
  .persona-why::before { content: ' — '; color: #ccc; }
  .persona-quirk { font-style: italic; color: #555; }
  .persona-boundary {
    display: inline-block;
    padding: 4px 10px; margin: 0 6px 6px 0;
    border-radius: 16px; font-size: 0.82rem;
    background: #F5F1EC; color: #6B5D4B;
  }
  .persona-shared-note {
    background: #FFF8EF; border-left: 3px solid #E8845A;
    padding: 12px 16px; border-radius: 8px;
    font-size: 0.85rem; color: #6B5D4B; line-height: 1.5;
    margin-bottom: 20px;
  }
  .persona-shared-note a { color: #C66232; }
`;

// Mild-dislike styling cue — same shape as likes but a muted background
// so admin reading at a glance can tell which list is which without
// re-reading the section header.
const dislikesSectionStyle = { background: '#FAFAF8' };

function LikeList({ items }) {
  if (!items || items.length === 0) {
    return <div className="empty-state">No likes configured.</div>;
  }
  return (
    <ul className="persona-list">
      {items.map((it, i) => (
        <li key={i}>
          <span className="persona-topic">
            {typeof it === 'string' ? it : it.topic}
          </span>
          {typeof it === 'object' && it.why && (
            <span className="persona-why">{it.why}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function QuirkList({ items }) {
  if (!items || items.length === 0) {
    return <div className="empty-state">No quirks configured.</div>;
  }
  return (
    <ul className="persona-list">
      {items.map((q, i) => (
        <li key={i}><span className="persona-quirk">{q}</span></li>
      ))}
    </ul>
  );
}

function BoundaryChips({ items }) {
  if (!items || items.length === 0) {
    return <div className="empty-state">No boundaries configured.</div>;
  }
  return (
    <div>
      {items.map((b, i) => (
        <span key={i} className="persona-boundary">{b}</span>
      ))}
    </div>
  );
}

export default function AdminUserPersona() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Two parallel requests — user header + persona payload.
        const [u, p] = await Promise.all([
          adminApi.getUser(id),
          adminGet(`/users/${id}/persona`),
        ]);
        if (cancelled) return;
        setUser(u);
        setPersona(p);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <AdminLayout title="Persona">
      <style>{styles}</style>

      <Link to={`/admin/users/${id}`} className="back-link">← Back to user</Link>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {user && (
        <div className="page-header">
          <div className="page-title">
            {user.name || '(unnamed)'}<span className="ver">{VERSION}</span>
          </div>
          <div className="page-subtitle">
            {user.email} · Persona sub-page
          </div>
        </div>
      )}

      {persona && (
        <>
          <div className="persona-shared-note">
            This is the AmigoMio default companion persona — currently shared
            across all users. Per-user persona tuning is a future story
            (#362 Phase B). What you see here is what Berta carries into this
            user's call as her inner-life profile.
          </div>

          <div className="section-title">
            Things she loves
            <span className="section-sub">{(persona.likes || []).length} item(s)</span>
          </div>
          <div className="persona-section">
            <LikeList items={persona.likes} />
          </div>

          <div className="section-title">
            Things she doesn't really get
            <span className="section-sub">
              mild dislikes · {(persona.dislikes || []).length} item(s)
            </span>
          </div>
          <div className="persona-section" style={dislikesSectionStyle}>
            <LikeList items={persona.dislikes} />
          </div>

          <div className="section-title">
            Quirks
            <span className="section-sub">{(persona.quirks || []).length} item(s)</span>
          </div>
          <div className="persona-section">
            <QuirkList items={persona.quirks} />
          </div>

          <div className="section-title">
            Boundaries
            <span className="section-sub">
              topics she gently steps back from — deliberate, not an oversight
            </span>
          </div>
          <div className="persona-section">
            <BoundaryChips items={persona.boundaries} />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
